/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Accessibility } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAccessibility } from '@/lib/accessibility';
import { toast } from 'sonner';

/**
 * SeniorModeToggle — one-click accessibility switch surfaced in the header.
 *
 * When ON:
 *  • elderly mode CSS class on <html> (already handled by AccessibilityProvider)
 *  • text scale bumped to 150 %
 *  • high contrast forced
 *  • elements with class `.hide-in-senior` disappear (see index.css)
 *
 * When OFF: preferences are restored to previous values.
 */
export const SeniorModeToggle = ({ compact = false }) => {
  const a11y = useAccessibility();
  const on = !!a11y.elderlyMode;

  const toggle = () => {
    if (on) {
      a11y.setElderlyMode(false);
      a11y.setHighContrast(false);
      a11y.setTextScale(100);
      toast.info('Modalità senior disattivata');
    } else {
      a11y.setElderlyMode(true);
      a11y.setHighContrast(true);
      a11y.setTextScale(150);
      toast.success('Modalità senior attiva — testo +150 %, alto contrasto');
    }
  };

  if (compact) {
    return (
      <Button
        variant={on ? 'default' : 'ghost'}
        size="icon"
        onClick={toggle}
        aria-pressed={on}
        aria-label={on ? 'Disattiva modalità senior' : 'Attiva modalità senior'}
        title={on ? 'Modalità senior attiva' : 'Attiva modalità senior (+150 % testo)'}
        data-testid="senior-mode-toggle"
      >
        <Accessibility className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Button
      variant={on ? 'default' : 'outline'}
      size="sm"
      onClick={toggle}
      aria-pressed={on}
      data-testid="senior-mode-toggle"
    >
      <Accessibility className="h-4 w-4 mr-2" />
      {on ? 'Senior attiva' : 'Modalità senior'}
    </Button>
  );
};

export default SeniorModeToggle;
